import { dateKey, isHourLocked, operatingDate, validateDate, validateHour, validateHours } from './validation'
import { isIsoInstant } from './isoInstant'
import { createOperation, deletePolicy, fetchCapacity, fetchPolicies, replaceOperation } from './apis'
import { AllocationPriorityApiError, POLICY_ERROR_MESSAGE } from './errors'
import {
  CUT_TYPE,
  dayExceptionName,
  MAX_SCENARIO_NAME_LENGTH,
  MAX_WAIT_MINUTES,
  POLICY_PRIORITY,
  SAVED_PRIORITY_TYPE,
  SHIPPER_CODE,
} from './consts'
import { HOURS_PER_DAY, hourToSlotRange, indexSlotsByHour } from './slots'
import type {
  AllocationPriorityApi,
  CapacityResponse,
  CapacitySnapshot,
  CapacityValue,
  HourPriority,
  OutboundPolicyResponse,
  PolicySlotRequest,
  PolicySlotResponse,
  PolicyWarehouseRequest,
  PolicyWarehouseResponse,
  PrioritySnapshot,
  PriorityScenario,
  ReplaceOperationRequest,
  ScenarioInput,
} from './types'

export const REAL_API_MESSAGE = {
  ORDER_INVALID: '창고 순위 형식이 올바르지 않습니다. 새로고침 후 다시 확인하세요.',
  RESET_BASE_UNSUPPORTED: '기본 우선순위 초기화는 지원하지 않습니다.',
  NOT_LOADED: '최신 정책을 다시 조회한 뒤 저장하세요.',
  BASE_MISSING: '기본 정책을 찾을 수 없습니다.',
  BASE_INCOMPLETE: '기본 정책의 시간 구간이 24시간을 모두 덮지 않습니다.',
  SCENARIO_EMPTY: '기본값과 다른 시간대가 없는 운영은 새로 등록할 수 없습니다.',
  SOURCE_SLOT_MISSING: '복사할 기존 정책 구간을 찾을 수 없습니다.',
  POLICY_INVALID: '정책 응답 형식이 올바르지 않습니다.',
  WAIT_RANGE: `대기시간은 0~${MAX_WAIT_MINUTES}분 정수로 입력하세요.`,
} as const

/** REQ-09: 클라이언트 가드 문구. */
export const GUARD_MESSAGE = {
  NAME_LENGTH: `운영명은 1~${MAX_SCENARIO_NAME_LENGTH}자로 입력하세요.`,
  PERIOD_REVERSED: '기간 시작일은 종료일 이하여야 합니다.',
  PAST_PERIOD: '과거 기간은 저장할 수 없습니다.',
  SCENARIO_NOT_FOUND: '운영을 찾을 수 없습니다.',
  SCENARIO_STARTED: '과거에 시작한 운영은 수정할 수 없습니다.',
  PERIOD_OVERLAP: '다른 운영과 기간이 충돌합니다.',
  EXCEPTION_TODAY_ONLY: '예외는 당일만 저장할 수 있습니다.',
  HOUR_LOCKED: '지난 시간은 수정할 수 없습니다.',
} as const

const SCOPE = { shipperCode: SHIPPER_CODE, cutType: CUT_TYPE }

/** 마지막 성공 read 의 원본. 모든 쓰기의 version·창고 복사 원본이다. */
interface LoadedPolicies {
  base: OutboundPolicyResponse
  scenarios: Map<string, OutboundPolicyResponse>
  days: Map<string, OutboundPolicyResponse>
  slotsById: Map<number, Map<number, PolicySlotResponse>>
  /** 순위 대상 창고 코드 (BASE 전체 집합, 코드 오름차순). HourPriority.order 는 이 인덱스의 순열이다. */
  targets: string[]
}

type Warehouses = PolicyWarehouseResponse[]

const byCode = (a: string, b: string): number => {
  if (a < b) return -1
  return a > b ? 1 : 0
}

/** read 에서 배열임을 검증했다 (REQ-10). */
const warehousesOf = (slot: PolicySlotResponse): Warehouses => slot.values.warehouses as Warehouses

const isRanked = (w: PolicyWarehouseResponse): w is PolicyWarehouseResponse & { priority: number } => typeof w.priority === 'number'

/** 정렬된 코드 집합. 중복 코드는 형식 오류 (원소 형식은 assertSlotShape 가 검사). */
const codeSetOf = (warehouses: Warehouses): string[] => {
  const codes = warehouses.map((w) => w.warehouseCode).toSorted(byCode)
  if (codes.some((c, i) => i > 0 && codes[i - 1] === c)) throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
  return codes
}

const sameList = <T>(a: readonly T[], b: readonly T[]): boolean => a.length === b.length && a.every((v, i) => v === b[i])

/**
 * REQ-13: BASE targets 기준으로 정규화한다. 순위 있는 창고를 priority 오름차순으로 앞에, 나머지 targets 를 코드 오름차순으로 뒤에.
 * 슬롯에 없는 BASE 코드 = 순위 없음, BASE 에 없는 슬롯 코드는 무시. order 는 targets 인덱스, ranked 는 순위 있는 창고 수.
 */
const rankOf = (slot: PolicySlotResponse, targets: string[]): { order: number[]; ranked: number } => {
  const ranked = warehousesOf(slot)
    .filter((w) => isRanked(w) && targets.includes(w.warehouseCode))
    .toSorted((a, b) => (a.priority as number) - (b.priority as number) || byCode(a.warehouseCode, b.warehouseCode))
    .map((w) => targets.indexOf(w.warehouseCode))
  const unranked = targets.map((_, i) => i).filter((i) => !ranked.includes(i))
  return { order: [...ranked, ...unranked], ranked: ranked.length }
}

/** priority 는 null/생략(순위 없음) 또는 1 이상 정수. 문자열·0·음수·소수는 형식 오류. */
const isPriorityValue = (p: unknown): boolean => p === undefined || p === null || (typeof p === 'number' && Number.isInteger(p) && p >= 1)

const isWarehouse = (w: unknown): w is PolicyWarehouseResponse =>
  typeof w === 'object' &&
  w !== null &&
  typeof (w as PolicyWarehouseResponse).warehouseCode === 'string' &&
  !!(w as PolicyWarehouseResponse).warehouseCode.trim() &&
  isPriorityValue((w as PolicyWarehouseResponse).priority)

/** REQ-10: 비 nullable 필드 누락은 기본값으로 메우지 않고 조회 실패. 창고 원소 형식·코드 중복도 검사한다. */
const assertSlotShape = (slot: PolicySlotResponse): void => {
  const warehouses = slot.values?.warehouses
  if (typeof slot.values?.waitTimeMinutes !== 'number' || !Array.isArray(warehouses) || !warehouses.every(isWarehouse))
    throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
  codeSetOf(warehouses)
}

/** 슬롯 창고 집합이 BASE 전체 집합과 같은가. 다르면 저장 시 항상 재작성한다. */
const alignedWith = (slot: PolicySlotResponse, targets: string[]): boolean => sameList(codeSetOf(warehousesOf(slot)), targets)

/**
 * REQ-12: BASE 슬롯 집합 불일치는 조회 실패. SPECIAL/DAY 는 BE 가 재동기화하지 않으므로 실패시키지 않고 정규화한다.
 */
const toHourPriority = (slot: PolicySlotResponse, targets: string[], strict: boolean): HourPriority => {
  assertSlotShape(slot)
  if (strict && !alignedWith(slot, targets)) throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
  return { ...rankOf(slot, targets), wait: slot.values.waitTimeMinutes as number }
}

/**
 * REQ-15: order 는 targets 인덱스 순열, ranked 는 0..targets.length.
 * 대기시간은 BE 계약(정수 0~540)만 본다. 서버 값은 30분 단위가 아닐 수 있고(예: 5분), 회송하는 미편집 슬롯도 이 값을 그대로 보낸다.
 * 사용자가 바꾼 값의 30분 단위는 화면(wiring) 검증이 맡는다.
 */
const assertSavable = (value: HourPriority, targets: string[]): void => {
  const n = targets.length
  const { order } = value
  const permutation =
    Array.isArray(order) && order.length === n && order.every((i) => Number.isInteger(i) && i >= 0 && i < n) && new Set(order).size === n
  const { ranked } = value
  if (ranked === undefined || !permutation || !Number.isInteger(ranked) || ranked < 0 || ranked > n) throw new Error(REAL_API_MESSAGE.ORDER_INVALID)
  if (!Number.isInteger(value.wait) || value.wait < 0 || value.wait > MAX_WAIT_MINUTES) throw new Error(REAL_API_MESSAGE.WAIT_RANGE)
}

const rewrite = (targets: string[], order: number[], ranked: number): PolicyWarehouseRequest[] =>
  order.map((index, position) => ({ warehouseCode: targets[index], priority: position < ranked ? position + 1 : null }))

/**
 * REQ-15: 화면 order·ranked 가 원본 슬롯에서 읽은 값과 같고 원본 창고 집합이 BASE 와 같으면 원본 warehouses 회송(priority 숫자 보존).
 * 그 외(값 변경 또는 집합 불일치 슬롯)는 targets 전체 코드, order 앞 ranked 개는 위치+1, 나머지 null.
 * value 가 없으면(회송) 읽은 order·ranked 를 쓴다.
 */
const warehousesFor = (source: PolicySlotResponse, targets: string[], value?: HourPriority): PolicyWarehouseRequest[] => {
  const read = rankOf(source, targets)
  if (value) {
    // 순위→순위 없음 내림 금지는 드래그 UI 규칙이다. 복사·불러오기는 다른 슬롯 값을 통째로 넣으므로 원본 슬롯과 비교하지 않는다.
    // assertSavable 이 ranked 누락을 먼저 거절한다. 여기서도 단언 대신 다시 막아 null 일괄 저장을 원천 차단한다.
    if (value.ranked === undefined) throw new Error(REAL_API_MESSAGE.ORDER_INVALID)
    if (!(sameList(value.order, read.order) && value.ranked === read.ranked)) return rewrite(targets, value.order, value.ranked)
  }
  if (!alignedWith(source, targets)) return rewrite(targets, read.order, read.ranked)
  return warehousesOf(source).map((w) => ({ warehouseCode: w.warehouseCode, priority: w.priority ?? null }))
}

/**
 * 전송 슬롯: 시간 단위 구간, priorityType 은 항상 PACKING (REQ-06).
 * warehousePriorityEnabled 는 원본 슬롯에서 그대로 복사 (누락 = null). warehouses 는 warehousesFor (isEnabled 제외).
 * value 가 없으면(회송) 원본 대기시간·창고를 그대로 보낸다.
 */
const toSlotRequest = (hour: number, source: PolicySlotResponse, targets: string[], value?: HourPriority): PolicySlotRequest => ({
  ...hourToSlotRange(hour),
  values: {
    waitTimeMinutes: value ? value.wait : (source.values.waitTimeMinutes as number),
    priorityType: SAVED_PRIORITY_TYPE,
    warehousePriorityEnabled: source.values.warehousePriorityEnabled ?? null,
    warehouses: warehousesFor(source, targets, value),
  },
})

/** LOGISPRD-14585: BASE 슬롯0 의 warehouseName(trim). 누락·공백·비문자열이면 코드. 이름은 표시 전용이라 키로 쓰지 않는다. */
const labelOf = (slot: PolicySlotResponse, code: string): string => {
  const name: unknown = warehousesOf(slot).find((w) => w.warehouseCode === code)?.warehouseName
  const trimmed = typeof name === 'string' ? name.trim() : ''
  return trimmed || code
}

const bySecond = (a: PolicySlotRequest, b: PolicySlotRequest): number => a.startSecond - b.startSecond

/** 응답 → 스냅샷 + 보관 상태. 순수 함수라 실패 시 기존 보관 상태를 건드리지 않는다. */
const buildLoaded = (policies: OutboundPolicyResponse[]): { snapshot: PrioritySnapshot; loaded: LoadedPolicies } => {
  const active = policies.filter((p) => !p.isDeleted)
  const bases = active.filter((p) => p.priority === POLICY_PRIORITY.BASE)
  if (bases.length !== 1) throw new Error(REAL_API_MESSAGE.BASE_MISSING)
  const slotsById = new Map<number, Map<number, PolicySlotResponse>>()
  active.forEach((p) => slotsById.set(p.id, indexSlotsByHour(p.slots ?? [])))
  const base = bases[0]
  const baseSlots = slotsById.get(base.id) as Map<number, PolicySlotResponse>
  if (baseSlots.size !== HOURS_PER_DAY) throw new Error(REAL_API_MESSAGE.BASE_INCOMPLETE)
  // REQ-12: targets = BASE 창고 코드 집합. BASE 슬롯마다 다르면 조회 실패 (toHourPriority strict).
  const firstBase = baseSlots.get(0)
  if (!firstBase) throw new Error(REAL_API_MESSAGE.BASE_INCOMPLETE)
  assertSlotShape(firstBase)
  const targets = codeSetOf(warehousesOf(firstBase))

  const hoursOf = (p: OutboundPolicyResponse): Record<number, HourPriority> => {
    const strict = p.priority === POLICY_PRIORITY.BASE
    const hours: Record<number, HourPriority> = {}
    ;(slotsById.get(p.id) as Map<number, PolicySlotResponse>).forEach((slot, hour) => {
      hours[hour] = toHourPriority(slot, targets, strict)
    })
    return hours
  }

  const scenarios = new Map<string, OutboundPolicyResponse>()
  const days = new Map<string, OutboundPolicyResponse>()
  const snapshot: PrioritySnapshot = {
    base: hoursOf(base),
    scenarios: [],
    exceptions: {},
    targets: targets.map((code) => ({ code, label: labelOf(firstBase, code) })),
  }
  active.forEach((p) => {
    if (p.priority === POLICY_PRIORITY.BASE) return
    if (!p.startDate || !p.endDate) throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
    if (p.priority === POLICY_PRIORITY.SPECIAL) {
      const scenario: PriorityScenario = { id: String(p.id), name: p.name, from: p.startDate, to: p.endDate, hours: hoursOf(p) }
      scenarios.set(scenario.id, p)
      snapshot.scenarios.push(scenario)
    } else if (p.priority === POLICY_PRIORITY.DAY) {
      // REQ-10: DAY 는 하루짜리이고 날짜당 하나다.
      if (p.startDate !== p.endDate || days.has(p.startDate)) throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
      days.set(p.startDate, p)
      Object.entries(hoursOf(p)).forEach(([hour, value]) => {
        snapshot.exceptions[`${p.startDate}|${hour}`] = value
      })
    } else {
      throw new Error(REAL_API_MESSAGE.POLICY_INVALID)
    }
  })
  return { snapshot, loaded: { base, scenarios, days, slotsById, targets } }
}

const countOf = (v: number | null | undefined): number | null => (typeof v === 'number' ? v : null)
/** LOGISPRD-14579 R1: mode·target·used 를 보존한다. available 은 LIMITED 의 LES 원문만(UNLIMITED 는 null). */
const capacityValueOf = (w: NonNullable<CapacityResponse['warehouses']>[number]): CapacityValue => ({
  unlimited: w.mode === 'UNLIMITED',
  target: countOf(w.target),
  used: countOf(w.used),
  available: w.mode === 'UNLIMITED' ? null : countOf(w.available),
})

/**
 * REQ-18: 응답 → 창고 코드별 CAPA. UNAVAILABLE 은 unavailable=true(모든 창고 표시 없음). stale 이어도 값 그대로.
 * 운영일은 응답 operationDate(BE 계약: 요청 조회 축)를 쓴다. 요청 날짜와 다르면 형식 오류(일반 실패), 생략이면 요청 날짜.
 */
export const toCapacitySnapshot = (response: CapacityResponse, requestedDate: string): CapacitySnapshot => {
  const operationDate = response.operationDate ?? requestedDate
  if (operationDate !== requestedDate) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  const unavailable = response.status === 'UNAVAILABLE'
  const warehouses: Record<string, CapacityValue> = {}
  if (!unavailable)
    (response.warehouses ?? []).forEach((w) => {
      warehouses[w.warehouseCode] = capacityValueOf(w)
    })
  // LOGISPRD-14585: fetchedAt 형식 오류는 응답 전체를 거부하지 않고 null(표시 없음)로 둔다.
  const raw: unknown = response.fetchedAt
  const fetchedAt = !unavailable && isIsoInstant(raw) ? raw : null
  return { operationDate, unavailable, stale: response.stale === true, fetchedAt, warehouses }
}

/** 화면 행 1개의 CAPA. UNAVAILABLE·응답에 없는 창고 → null(표시 없음, LOGISPRD-14579 R5). */
export const capacityOf = (snapshot: CapacitySnapshot, warehouseCode: string): CapacityValue | null => {
  if (snapshot.unavailable) return null
  return snapshot.warehouses[warehouseCode] ?? null
}

export function createAllocationPriorityRealApi({ now = () => new Date() }: { now?: () => Date } = {}): AllocationPriorityApi {
  let loaded: LoadedPolicies | undefined
  /** 가장 마지막에 시작한 read 만 보관 상태를 교체한다. 쓰기 성공도 세대를 올려 그 이전 read 를 무효화한다. */
  let generation = 0

  const todayKey = (): string => dateKey(operatingDate(now()))

  const current = (): LoadedPolicies => {
    if (!loaded) throw new Error(REAL_API_MESSAGE.NOT_LOADED)
    return loaded
  }
  /** 쓰기 성공 후 version 이 바뀌었으므로 다음 쓰기 전에 재조회를 강제한다. */
  const afterWrite = (): void => {
    loaded = undefined
    generation += 1
  }

  const ownSlots = (state: LoadedPolicies, policy: OutboundPolicyResponse): Map<number, PolicySlotResponse> =>
    state.slotsById.get(policy.id) ?? new Map<number, PolicySlotResponse>()

  /** 해당 날짜를 포함하는 SPECIAL (같은 층 기간은 겹치지 않는다). */
  const specialCovering = (state: LoadedPolicies, date: string): OutboundPolicyResponse | undefined =>
    Array.from(state.scenarios.values()).find((p) => (p.startDate as string) <= date && date <= (p.endDate as string))

  /** 복사 원본: 같은 정책 같은 시간 → (DAY) 해당 날짜 SPECIAL 같은 시간 → BASE 같은 시간. */
  const newSlot = (
    state: LoadedPolicies,
    hour: number,
    value: HourPriority,
    { own, dayDate }: { own?: OutboundPolicyResponse; dayDate?: string } = {},
  ): PolicySlotRequest => {
    const special = dayDate === undefined ? undefined : specialCovering(state, dayDate)
    const source =
      (own && ownSlots(state, own).get(hour)) ?? (special && ownSlots(state, special).get(hour)) ?? ownSlots(state, state.base).get(hour)
    if (!source) throw new Error(REAL_API_MESSAGE.SOURCE_SLOT_MISSING)
    return toSlotRequest(hour, source, state.targets, value)
  }

  /** 자기 슬롯을 시간 단위로 회송한다 (except 시간 제외). */
  const echoOwn = (state: LoadedPolicies, policy: OutboundPolicyResponse, except?: number): PolicySlotRequest[] =>
    Array.from(ownSlots(state, policy).entries())
      .filter(([h]) => h !== except)
      .map(([h, slot]) => toSlotRequest(h, slot, state.targets))

  const replaceBody = (
    state: LoadedPolicies,
    policy: OutboundPolicyResponse,
    slots: PolicySlotRequest[],
    name = policy.name,
  ): ReplaceOperationRequest => {
    const body: ReplaceOperationRequest = {
      ...SCOPE,
      expectedVersion: policy.version,
      expectedBaseVersion: state.base.version,
      name,
      slots: [...slots].sort(bySecond),
    }
    if (policy.priority !== POLICY_PRIORITY.BASE) {
      body.startDate = policy.startDate ?? undefined
      body.endDate = policy.endDate ?? undefined
    }
    return body
  }

  /** 없으면 오류, 이미 시작한 운영은 수정 불가. */
  const scenarioFor = (state: LoadedPolicies, id: string): OutboundPolicyResponse => {
    const found = state.scenarios.get(id)
    if (!found) throw new Error(GUARD_MESSAGE.SCENARIO_NOT_FOUND)
    if ((found.startDate as string) < todayKey()) throw new Error(GUARD_MESSAGE.SCENARIO_STARTED)
    return found
  }

  /** 운영일 당일만, 지난 시간 잠금. */
  const editableException = (date: string, hour: number): void => {
    validateDate(date)
    validateHour(hour)
    if (date !== todayKey()) throw new Error(GUARD_MESSAGE.EXCEPTION_TODAY_ONLY)
    if (isHourLocked(date, hour, now())) throw new Error(GUARD_MESSAGE.HOUR_LOCKED)
  }

  const saveExceptions: AllocationPriorityApi['saveExceptions'] = async (date, hours, value) => {
    validateDate(date)
    validateHours(hours)
    hours.forEach((hour) => editableException(date, hour))
    const state = current()
    assertSavable(value, state.targets)
    const day = state.days.get(date)
    const slots = hours.map((hour) => newSlot(state, hour, value, { own: day, dayDate: date }))
    if (day) {
      const kept = Array.from(ownSlots(state, day).entries())
        .filter(([h]) => !hours.includes(h))
        .map(([h, slot]) => toSlotRequest(h, slot, state.targets))
      await replaceOperation(day.id, replaceBody(state, day, [...kept, ...slots]))
    } else {
      await createOperation({
        ...SCOPE,
        name: dayExceptionName(date),
        priority: POLICY_PRIORITY.DAY,
        startDate: date,
        endDate: date,
        expectedBaseVersion: state.base.version,
        slots: slots.toSorted(bySecond),
      })
    }
    afterWrite()
  }

  return {
    now,
    async read() {
      generation += 1
      const issued = generation
      const policies = await fetchPolicies()
      const { snapshot, loaded: next } = buildLoaded(policies)
      if (issued === generation) loaded = next
      return snapshot
    },
    async saveBase(hour, value) {
      validateHour(hour)
      const state = current()
      assertSavable(value, state.targets)
      const slots = Array.from(ownSlots(state, state.base).entries()).map(([h, slot]) =>
        toSlotRequest(h, slot, state.targets, h === hour ? value : undefined),
      )
      await replaceOperation(state.base.id, replaceBody(state, state.base, slots))
      afterWrite()
    },
    /** REQ-17: 운영일 CAPA. 실패는 그대로 던진다 (Mock fallback·재시도 없음). */
    async capacity(date) {
      validateDate(date)
      return toCapacitySnapshot(await fetchCapacity(date), date)
    },
    /** 서버에 BASE 초기값 개념이 없다. */
    resetBase: () => Promise.reject(new Error(REAL_API_MESSAGE.RESET_BASE_UNSUPPORTED)),
    async saveScenario(value: ScenarioInput) {
      validateDate(value.from)
      validateDate(value.to)
      const name = value.name.trim()
      if (!name || name.length > MAX_SCENARIO_NAME_LENGTH) throw new Error(GUARD_MESSAGE.NAME_LENGTH)
      if (value.from > value.to) throw new Error(GUARD_MESSAGE.PERIOD_REVERSED)
      if (value.from < todayKey()) throw new Error(GUARD_MESSAGE.PAST_PERIOD)
      const state = current()
      const policy = value.id === undefined ? undefined : scenarioFor(state, value.id)
      const overlaps = Array.from(state.scenarios.entries()).some(
        ([id, p]) => id !== value.id && (p.startDate as string) <= value.to && value.from <= (p.endDate as string),
      )
      if (overlaps) throw new Error(GUARD_MESSAGE.PERIOD_OVERLAP)
      const entries = Object.entries(value.hours).map(([hr, priority]) => {
        const hour = Number(hr)
        validateHour(hour)
        assertSavable(priority, state.targets)
        return [hour, priority] as const
      })
      const slots = entries.map(([hour, priority]) => newSlot(state, hour, priority, { own: policy }))
      if (!policy) {
        if (slots.length === 0) throw new Error(REAL_API_MESSAGE.SCENARIO_EMPTY)
        const created = await createOperation({
          ...SCOPE,
          name,
          priority: POLICY_PRIORITY.SPECIAL,
          startDate: value.from,
          endDate: value.to,
          expectedBaseVersion: state.base.version,
          slots: slots.toSorted(bySecond),
        })
        afterWrite()
        return String(created.id)
      }
      await replaceOperation(policy.id, { ...replaceBody(state, policy, slots, name), startDate: value.from, endDate: value.to })
      afterWrite()
      return String(policy.id)
    },
    async resetScenario(id, hour) {
      const state = current()
      const policy = scenarioFor(state, id)
      if (hour !== undefined) validateHour(hour)
      const slots = hour === undefined ? [] : echoOwn(state, policy, hour)
      await replaceOperation(policy.id, replaceBody(state, policy, slots))
      afterWrite()
    },
    async deleteScenario(id) {
      const state = current()
      const policy = scenarioFor(state, id)
      await deletePolicy(policy.id, { ...SCOPE, expectedVersion: policy.version, expectedBaseVersion: state.base.version })
      afterWrite()
    },
    saveException(date, hour, value) {
      return saveExceptions(date, [hour], value)
    },
    /** REQ-25: 범위 저장. DAY 정책 단위 요청 1회 (시간별 순차 호출 없음). */
    saveExceptions,
    async resetException(date, hour) {
      editableException(date, hour)
      const state = current()
      const day = state.days.get(date)
      // 대상 예외가 없으면 요청 없이 성공.
      if (!day || !ownSlots(state, day).has(hour)) return
      await replaceOperation(day.id, replaceBody(state, day, echoOwn(state, day, hour)))
      afterWrite()
    },
  }
}
